import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Sparkles, Download, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section, Empty, StatusBadge } from "@/components/kit";
import SecureImage from "@/components/SecureImage";
import useLoad from "@/lib/useLoad";
import api, { errMsg, fmtDate, money, downloadFile } from "@/lib/api";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function habitFrequencyLabel(habit) {
  if (habit.frequency !== "specific_days") return "Daily";
  const days = (habit.days_of_week || []).map((day) => DAYS[day]).filter(Boolean);
  return `Specific days · ${days.join(", ")}`;
}

export function PhotosTab({ client }) {
  const [photos] = useLoad("/photos", { client_id: client.id });
  if (!photos) return null;
  return (
    <Section title="Progress photos" testid="client-photos">
      <p className="text-xs text-slate-500 mb-4">Private: stored in the app database, never publicly addressable, visible only to the client and you. Each view is audit-logged.</p>
      {!photos.length ? <Empty text="No photos uploaded." /> : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {photos.map((p) => (
            <div key={p.id}><SecureImage photoId={p.id} className="w-full aspect-[3/4] object-cover rounded" />
              <div className="text-xs text-slate-500 mt-1">{p.taken_on} · {p.pose}</div></div>
          ))}
        </div>
      )}
    </Section>
  );
}

function HabitItem({ habit, onDelete, onToggle, onLog, today }) {
  const [amount, setAmount] = useState(String(habit.increment_amount || 1));
  const done = today && habit.done_dates.includes(today);
  const tracksAmount = ["quantity", "limit"].includes(habit.kind);
  const amountToday = Number(habit.amount_today || 0);
  const targetAmount = Number(habit.target_amount || 0);
  const hasTargetAmount = targetAmount > 0;
  const dueToday = habit.is_due_today !== false;
  const timing = habit.timing_mode === "specific_time" ? `Specific time · ${habit.scheduled_time}` : "Any time";
  const amountProgress = tracksAmount
    ? habit.kind === "limit" ? `${amountToday} / ${targetAmount} ${habit.unit} today · max` : hasTargetAmount ? `${amountToday} / ${targetAmount} ${habit.unit} today` : `${amountToday}${habit.unit ? ` ${habit.unit}` : ""} today`
    : `${habit.done_dates.length} of last 21 days`;
  const detail = [amountProgress, habitFrequencyLabel(habit), timing, habit.reminder_times?.length ? `Reminders ${habit.reminder_times.join(", ")}` : ""].filter(Boolean).join(" · ");
  return (
    <div className="flex flex-col gap-3 border-t border-[#1E293B] py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`habit-${habit.id}`}>
      <div className="flex min-w-0 items-center gap-3">
        {onToggle && !tracksAmount && (
          <button onClick={() => onToggle(habit)} disabled={!dueToday} data-testid={`habit-toggle-${habit.id}`} aria-label={`${done ? "Undo" : "Complete"} ${habit.name}`}
            className={`w-6 h-6 shrink-0 rounded border grid place-items-center transition-colors ${done ? "bg-emerald-500 border-emerald-500" : "border-slate-600 hover:border-emerald-400"}`}>
            {done && <Check size={14} />}
          </button>
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{habit.name}</div>
          <div className="text-xs text-slate-500">{detail}</div>
        </div>
      </div>
      {tracksAmount && onLog && (
        <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
          <Input type="number" min="0" step="any" value={amount} onChange={(event) => setAmount(event.target.value)} disabled={!dueToday} className="w-24" aria-label={`Amount to log for ${habit.name}`} data-testid={`habit-log-amount-${habit.id}`} />
          <Button type="button" size="sm" variant="outline" onClick={() => onLog(habit, Number(amount))} disabled={!dueToday || !Number.isFinite(Number(amount)) || Number(amount) <= 0} data-testid={`habit-log-btn-${habit.id}`}>+{amount || 0} {habit.unit}</Button>
        </div>
      )}
      {onDelete && <Button variant="ghost" size="icon" onClick={() => onDelete(habit)} data-testid={`habit-delete-${habit.id}`}><Trash2 size={14} /></Button>}
    </div>
  );
}

export function HabitList({ habits, onDelete, onToggle, onLog, today }) {
  if (!habits?.length) return <Empty text="No habits set." />;
  return habits.map((habit) => <HabitItem key={habit.id} habit={habit} onDelete={onDelete} onToggle={onToggle} onLog={onLog} today={today} />);
}

export function HabitsTab({ client }) {
  const [data, reload] = useLoad(`/clients/${client.id}/habits`);
  const setCatalogEnabled = async (habit, enabled) => {
    try { await api.put(`/clients/${client.id}/habits/${habit.id}`, { enabled }); reload(); }
    catch (error) { toast.error(errMsg(error)); }
  };
  const setLegacyEnabled = async (habit, enabled) => {
    try { await api.put(`/habits/${habit.id}/enabled`, { enabled }); reload(); }
    catch (error) { toast.error(errMsg(error)); }
  };
  const row = (habit, enabled, onChange, testid) => (
    <div key={habit.id} className="flex items-center justify-between gap-4 border-t border-[#1E293B] py-3" data-testid={testid}>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{habit.name}</div>
        <div className="text-xs text-slate-500">{[
          habit.kind === "quantity" || habit.kind === "limit" ? habit.target_amount != null ? `${habit.target_amount} ${habit.unit} ${habit.kind === "limit" ? "maximum" : "daily goal"}` : `Track amount${habit.unit ? ` in ${habit.unit}` : ""}` : habit.target || "daily",
          habitFrequencyLabel(habit),
          habit.timing_mode === "specific_time" ? `Specific time · ${habit.scheduled_time}` : "Any time",
          habit.reminder_times?.length ? `Reminders ${habit.reminder_times.join(", ")}` : "",
        ].filter(Boolean).join(" · ")}</div>
      </div>
      <Switch checked={enabled} onCheckedChange={onChange} aria-label={`${enabled ? "Disable" : "Enable"} ${habit.name}`} />
    </div>
  );
  return (
    <Section title="Habits" testid="client-habits">
      {!data ? <div className="eyebrow">Loading…</div> : (
        <>
          {data.catalog.map((habit) => row(habit, habit.enabled, (enabled) => setCatalogEnabled(habit, enabled), `client-habit-${habit.id}`))}
          {data.legacy.length > 0 && <div className="eyebrow pt-4">Custom habits</div>}
          {data.legacy.map((habit) => row(habit, habit.active !== false, (enabled) => setLegacyEnabled(habit, enabled), `client-legacy-habit-${habit.id}`))}
          {!data.catalog.length && !data.legacy.length && <Empty text="No habits configured." />}
        </>
      )}
    </Section>
  );
}

export function ReminderEditor({ clientId, tzLabel }) {
  const [list, reload] = useLoad("/reminders", clientId ? { client_id: clientId } : undefined);
  const [f, setF] = useState({ title: "", time_local: "07:00", kind: "custom", days: [0, 1, 2, 3, 4, 5, 6] });
  const add = async () => {
    try { await api.post("/reminders", { ...f, client_id: clientId }); setF({ ...f, title: "" }); reload(); toast.success("Reminder added"); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleDay = (d) => setF({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d].sort() });
  return (
    <Section title="Reminders" testid="reminders-section">
      <p className="text-xs text-slate-500 mb-4">Times are in the client's local timezone ({tzLabel}). Delivered in-app.</p>
      <div className="flex flex-wrap gap-2 mb-3">
        <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Reminder title" className="w-64" data-testid="reminder-title-input" />
        <Input type="time" value={f.time_local} onChange={(e) => setF({ ...f, time_local: e.target.value })} className="w-32" data-testid="reminder-time-input" />
        <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>{["workout", "meal", "habit", "checkin", "custom"].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={add} disabled={!f.title} data-testid="reminder-add-btn">Add</Button>
      </div>
      <div className="flex gap-1 mb-4">
        {DAYS.map((d, i) => (
          <button key={d} onClick={() => toggleDay(i)} data-testid={`reminder-day-${i}`}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${f.days.includes(i) ? "bg-[#FF3B30] border-[#FF3B30]" : "border-slate-600 text-slate-400"}`}>{d}</button>
        ))}
      </div>
      {!list?.length ? <Empty text="No reminders." /> : list.map((r) => (
        <div key={r.id} className="flex justify-between items-center py-2 border-t border-[#1E293B]" data-testid={`reminder-${r.id}`}>
          <div className="text-sm">{r.title} <span className="font-mono-k text-xs text-slate-400 ml-2">{r.time_local} · {r.days.map((d) => DAYS[d]).join(" ")}</span></div>
          <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/reminders/${r.id}`); reload(); }}><Trash2 size={14} /></Button>
        </div>
      ))}
    </Section>
  );
}

export function RemindersTab({ client }) {
  return <ReminderEditor clientId={client.id} tzLabel={client.timezone} />;
}

function SummaryCard({ s, onSaved }) {
  const [text, setText] = useState(s.summary);
  const save = async (approved) => {
    try { await api.put(`/ai/summaries/${s.id}`, { summary: text, approved }); toast.success(approved ? "Summary approved" : "Saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`rounded-md p-5 ${s.approved ? "panel" : "ai-draft"}`} data-testid={`summary-${s.id}`}>
      <div className="flex justify-between items-center mb-3">
        <span className="eyebrow">{fmtDate(s.created_at)} · adherence {s.adherence_score ?? "-"}%</span>
        <StatusBadge status={s.approved ? "reviewed" : "ai_draft"} />
      </div>
      <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} data-testid={`summary-text-${s.id}`} />
      <div className="grid md:grid-cols-3 gap-4 mt-4 text-sm">
        {[["Highlights", s.highlights, "text-emerald-400"], ["Concerns", s.concerns, "text-red-400"], ["Suggested adjustments", s.suggested_adjustments, "text-cyan-400"]].map(([t, items, c]) => (
          <div key={t}><div className={`eyebrow ${c}`}>{t}</div><ul className="mt-2 space-y-1 text-slate-300 list-disc pl-4">{(items || []).map((i) => <li key={i}>{i}</li>)}</ul></div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <Button size="sm" variant="outline" onClick={() => save(undefined)} data-testid={`summary-save-${s.id}`}>Save edits</Button>
        {!s.approved && <Button size="sm" onClick={() => save(true)} data-testid={`summary-approve-${s.id}`}>Approve</Button>}
      </div>
    </div>
  );
}

export function SummaryTab({ client }) {
  const [list, reload] = useLoad("/ai/summaries", { client_id: client.id });
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try { await api.post(`/ai/progress-summary/${client.id}`); reload(); toast.success("AI summary drafted - review it below"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Section title="AI progress summaries" testid="client-summaries"
      action={<Button onClick={gen} disabled={busy} data-testid="generate-summary-btn"><Sparkles size={14} className="mr-1" />{busy ? "Analysing 30 days…" : "Generate summary"}</Button>}>
      {!list?.length ? <Empty text="No summaries yet. Generate one from the last 30 days of logs, check-ins and habits." /> :
        <div className="space-y-4">{list.map((s) => <SummaryCard key={s.id} s={s} onSaved={reload} />)}</div>}
    </Section>
  );
}

export function ClientBillingTab({ client }) {
  const [invoices, reload] = useLoad("/billing/invoices", { client_id: client.id });
  const [packages] = useLoad("/billing/packages");
  const [pkg, setPkg] = useState("");
  const [coupon, setCoupon] = useState("");
  const [paymentInterval, setPaymentInterval] = useState("full");
  const [paymentReminders, setPaymentReminders] = useState(true);
  const selectedPackage = packages?.find((item) => item.id === pkg);
  const setInstallmentReminders = async (invoice, enabled) => {
    try {
      await api.put(`/billing/installments/${invoice.installment_group_id}/reminders`, { enabled });
      toast.success(`Installment reminders ${enabled ? "enabled" : "disabled"}`);
      reload();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const create = async () => {
    try {
      const { data } = await api.post("/billing/invoices", {
        client_id: client.id, package_id: pkg, coupon_code: coupon || null,
        payment_interval_weeks: paymentInterval === "full" ? null : Number(paymentInterval),
        payment_reminders: paymentInterval !== "full" && paymentReminders,
      });
      toast.success(data.installment_count > 1 ? `${data.installment_count} installment invoices created` : "Invoice created - client can pay from their Membership page");
      reload();
    }
    catch (e) { toast.error(errMsg(e)); }
  };
  const m = client.end_date ? { package_name: client.package_name, status: client.status, start: client.start_date, end: client.end_date } : null;
  return (
    <div className="space-y-6">
      <Section title="Membership">
        {m ? <div className="text-sm" data-testid="client-membership-info">{m.package_name} · <StatusBadge status={m.status} /> · {fmtDate(m.start)} → {fmtDate(m.end)}</div> : <Empty text="No membership yet." />}
      </Section>
      <Section title="Create invoice">
        <div className="flex flex-wrap gap-2">
          <Select value={pkg} onValueChange={setPkg}>
            <SelectTrigger className="w-72" data-testid="client-invoice-package-select"><SelectValue placeholder="Package" /></SelectTrigger>
            <SelectContent>{(packages || []).filter((p) => p.active).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {money(p.price, p.currency)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={paymentInterval} onValueChange={setPaymentInterval}>
            <SelectTrigger className="w-44" aria-label="Payment schedule" data-testid="client-invoice-payment-schedule"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="full">Pay in full</SelectItem>
              <SelectItem value="4" disabled={!selectedPackage || selectedPackage.duration_weeks <= 4}>Every 4 weeks</SelectItem>
            </SelectContent>
          </Select>
          <Input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon (optional)" className="w-44" data-testid="client-invoice-coupon-input" />
          <Button onClick={create} disabled={!pkg || (paymentInterval !== "full" && selectedPackage?.duration_weeks <= Number(paymentInterval))} data-testid="client-invoice-create-btn">Create invoice</Button>
        </div>
        {paymentInterval !== "full" && (
          <div className="mt-3 space-y-2">
            {selectedPackage && (() => {
              const count = Math.ceil(selectedPackage.duration_weeks / Number(paymentInterval));
              return <p className="text-xs text-slate-400" data-testid="client-invoice-installment-summary">
                {selectedPackage.duration_weeks}-week membership · {count} installments of about {money(selectedPackage.price / count, selectedPackage.currency)} every {paymentInterval} weeks
              </p>;
            })()}
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <Switch checked={paymentReminders} onCheckedChange={setPaymentReminders} data-testid="client-invoice-payment-reminders" />
              Send client in-app reminders before each installment is due
            </label>
          </div>
        )}
      </Section>
      <Section title="Invoices">
        {!invoices?.length ? <Empty text="No invoices." /> : invoices.map((i) => (
          <div key={i.id} className="flex items-center justify-between py-2.5 border-t border-[#1E293B] text-sm" data-testid={`client-invoice-${i.id}`}>
            <div>{i.number} · {i.package_name} <span className="text-slate-500 text-xs ml-2">{fmtDate(i.created_at)}</span>
              {i.installment_count > 1 && <div className="text-xs text-slate-500">Installment {i.installment_number} of {i.installment_count} · due {fmtDate(i.due_date)}</div>}
              {i.installment_number === 1 && <label className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                <Switch checked={Boolean(i.payment_reminders)} onCheckedChange={(enabled) => setInstallmentReminders(i, enabled)} aria-label="Toggle installment reminders" data-testid={`installment-reminders-${i.installment_group_id}`} />
                Client in-app payment reminders
              </label>}
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono-k">{money(i.total, i.currency)}</span><StatusBadge status={i.status} />
              <Button variant="ghost" size="icon" onClick={() => downloadFile(`/billing/invoices/${i.id}/pdf`, `${i.number}.pdf`)}><Download size={14} /></Button>
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}
